/**
 * 安全周报 API 服务
 */
import http from './utils';
import { prefix } from './constants'; 
import { gameInfoType } from '@op/types/common';

// ========== 类型定义 ==========

export interface IWeeklyFilter {
  start_time: string;
  end_time: string;
  date_type?: 'day' | 'week';
  channel?: string[];
  language?: string[];
  topic?: string[];
  game_code?: string;
}

/**
 * 周报概览数据 - API 原始返回格式
 */
export interface IOverviewDataRaw {
  trend_time: {
    start_time: string;
    end_time: string;
  };
  link_time: {
    start_time: string;
    end_time: string;
  };
  summary: {
    mentions: {
      new: number;
      old: number;
    };
    engagement: {
      new: number;
      old: number;
    };
  };
  trend: IKeyMetricsTrendItem[];
}

/**
 * 周报概览数据 - 组件使用格式（经过 Hook 处理）
 */
export interface ICoreOverviewSummaryRequest {
  dtstatdate: string;
  edition_unified_id: string;
  entity_type: string;
  id_type: string;
}

export interface ICoreOverviewSummaryResponse {
  dtstatdate: string;
  report_cn: string;
  report_en?: string;
}

export interface IOverviewData {
  mention: number;
  mention_change_rate: number;
  engagement: number;
  engagement_change_rate: number;
  prev_mention: number;
  prev_engagement: number;
  start_time: string;
  end_time: string;
  prev_start_time: string;
  prev_end_time: string;
}

export interface IKeyMetricsTrendItem {
  date: string;
  mentions: number;
  total_mentions: number;
  mentions_ratio: number;
  new_add_keyword?: boolean;
}

export interface IKeyMetricsResponse {
  compare_period: {
    start_time: string;
    end_time: string;
  };
  summary: {
    mentions: {
      new: number;
      old: number;
    };
    engagement: {
      new: number;
      old: number;
    };
  };
  trend: IKeyMetricsTrendItem[];
}

// 子话题四象限接口请求参数
export interface ISubtopicQuadrantRequest {
  edition_unified_id: string;
  entity_type: string;
  id_type: string;
  date_type: string;
  start_time: string;
  end_time: string;
  channels?: string[];
  languages?: string[];
  categories?: string[];
  subcategories?: string[];
  sentiment_type?: string;
  channel_type?: string;
}

// 子话题四象限数据点
export interface ISubtopicQuadrantItem {
  sub_category: string; // 子话题（英文）
  category: string; // 一级话题（英文）
  sub_category_cn: string; // 子话题（中文）
  category_cn: string; // 一级话题（中文）
  mentions: number; // 当前周期声量
  avg_engagement_intensity: number; // 平均互动强度
  previous_period_mentions: number; // 上一周期声量
}

// 子话题四象限接口响应
export interface ISubtopicQuadrantResponse {
  chart_data: ISubtopicQuadrantItem[];
  link_time: {
    start_time: string;
    end_time: string;
  };
}

/**
 * @deprecated 请使用 ISubtopicQuadrantItem
 */
export interface IQuadrantPoint {
  topic_id: string;
  topic_name: string;
  topic_name_cn?: string;
  report_type: string;
  mention: number;
  avg_intensity: number;
  change_rate: number;
  quadrant: 1 | 2 | 3 | 4;
}

/**
 * @deprecated 请使用 ISubtopicQuadrantResponse
 */
export interface IQuadrantData {
  list: IQuadrantPoint[];
  thresholds: {
    x_axis: number; // 声量 60 分位
    y_axis: number; // 互动强度 50 分位
  };
  quadrant_summary: {
    quadrant: number;
    topics: string[];
  }[];
}

export interface ITrendItem {
  timeline_start_time: string;
  timeline_end_time: string;
  /** 链接时间范围 */
  link_time: {
    start_time: string;
    end_time: string;
  };
  /** 分组信息 */
  group_info: {
    group_by: string;
    total_groups: number;
  };
  /** 汇总信息 */
  summary: {
    top_contributor: {
      dimension_value: string;
      contribution_rate: number;
      volume_change: number;
      volume_ratio: number;
    };
    total_fluctuation: number;
  };
  /** 趋势数据（折线图） */
  trend_data: Array<{
    date: string;
    dimensions: Array<{
      dimension_value: string;
      volume: number;
    }>;
  }>;
  /** 饼图数据 */
  pie_data: Array<{
    dimension_value: string;
    volume: number;
    ratio: number;
  }>;
}

/**
 * 分举报类型声量趋势
 */
export interface ICategoryMentionsTrendItem {
  category_name: string;
  category_name_en: string;
  mentions: number;
}

export interface ICategoryMentionsTrendTrendItem {
  date: string;
  categories: ICategoryMentionsTrendItem[];
}

export interface ICategoryMentionsTrendListItem {
  category_name: string;
  category_name_en: string;
  mentions: number;
  mentions_ratio: number;
  wow_ratio: number;
}

export interface ICategoryMentionsTrendSummary {
  top_category_name: string;
  top_category_name_en: string;
  change_rate: number;
  contribution_rate: number;
}

export interface ICategoryMentionsTrendResponse {
  summary: ICategoryMentionsTrendSummary;
  list: ICategoryMentionsTrendListItem[];
  trend: ICategoryMentionsTrendTrendItem[];
  trend_time: {
    start_time: string;
    end_time: string;
  };
  link_time: {
    start_time: string;
    end_time: string;
  };
}

/**
 * 分子话题声量走势
 */
export interface ISubTopicTrendItem {
  date: string;
  category_cn: string;
  category_en: string;
  sub_category_cn: string;
  sub_category_en: string;
  mentions: number;
}

export interface ISubTopicTrendListItem {
  category_cn: string;
  category_en: string;
  sub_category_cn: string;
  sub_category_en: string;
  mentions: number;
  mentions_ratio: number;
}

export interface ISubTopicTrendSummary {
  category_cn: string;
  category_en: string;
  fluctuation_rate: number;
  contribution_rate: number;
  sub_category_cn: string;
  sub_category_en: string;
}

export interface ISubTopicTrendResponse {
  summary: ISubTopicTrendSummary;
  list: ISubTopicTrendListItem[];
  trend: ISubTopicTrendItem[];
  trend_time: {
    start_time: string;
    end_time: string;
  };
  link_time: {
    start_time: string;
    end_time: string;
  };
}

/**
 * 关键词列表项（list）
 */
export interface IKeywordItem {
  keyword: string;
  keyword_cn?: string;
  keyword_en?: string;
  mentions: number;
  old_mentions: number;
  avg_sentiment: number;
}

/**
 * 关键词汇总项（summary.top_fluctuation / sudden_keywords）
 */
export interface IKeywordSummaryItem {
  keyword: string;
  keyword_cn?: string;
  keyword_en?: string;
  mentions: number;
  old_mentions: number;
  change_ratio: number;
}

/**
 * 关键词趋势项（trend.keyword）
 * 注意：趋势数据不包含 old_mentions
 */
export interface IKeywordTrendItem {
  keyword: string;
  keyword_cn?: string;
  keyword_en?: string;
  mentions: number;
  avg_sentiment: number;
}

/**
 * 关键词接口响应
 */
export interface IKeywordResponse {
  /** 汇总信息 */
  summary: {
    /** 波动最大的关键词 TOP3 */
    top_fluctuation: IKeywordSummaryItem[];
    /** 突增关键词 TOP2 */
    sudden_keywords: IKeywordSummaryItem[];
  };
  /** 关键词列表 TOP30 */
  list: IKeywordItem[];
  /** 趋势数据（折线图） */
  trend: Array<{
    date: string;
    keyword: IKeywordTrendItem[];
  }>;
  /** 趋势时间范围 */
  trend_time: {
    start_time: string;
    end_time: string;
  };
  /** 对比时间范围 */
  link_time: {
    start_time: string;
    end_time: string;
  };
}

export interface IDetailTableRow {
  date: string;
  category: string; // 主话题
  subcategory: string; // 子话题
  language: string; // 语言
  channel: string; // 渠道
  current_volume: number; // 本周声量
  previous_volume: number; // 上周声量
  change_rate: number; // 环比变化率
  contribution_rate: number; // 波动变化率
}

export interface IDetailTableResponse {
  /** 链接时间范围 */
  link_time: {
    start_time: string;
    end_time: string;
  };
  dimension_config: {
    dimensions: string[];
  };
  summary: {
    top_fluctuation: {
      date: string;
      category: string;
      category_en: string;
      channel: string;
      change_rate: number;
      contribution_rate: number;
    };
    total_current_volume: number;
    total_previous_volume: number;
    total_change: number;
    total_change_rate: number;
  };
  /** 表格数据实际条数 */
  total: number;
  table_data: IDetailTableRow[];
}

// ========== 分类筛选器相关类型 ==========

/**
 * 二级分类项
 */
export interface ISubCategoryItem {
  sub_category_name: string;
  sub_category_name_en: string;
}

/**
 * 一级分类项
 */
export interface ICategoryItem {
  category_name: string;
  category_name_en: string;
  subcategories: ISubCategoryItem[];
}

/**
 * 分类筛选器接口响应
 */
export interface IFilterTopicResponse {
  categories: ICategoryItem[];
}

// ========== API 请求 ==========

/**
 * 获取核心概览摘要
 * API: POST /api/v1/opinion_pc/safety_weekly_report/core_overview_summary
 */
export const getCoreOverviewSummary = (config: {
  data: ICoreOverviewSummaryRequest;
  cancelToken?: any;
  headers?: any;
}): Promise<ICoreOverviewSummaryResponse> => {
  return http({
    url: `${prefix}/safety_weekly_report/core_overview_summary`,
    method: 'post',
    ...config,
  }) as Promise<ICoreOverviewSummaryResponse>;
};

/**
 * 安全周报-关键指标 关键指标趋势
 */
export function getWeeklyOverview(payload: any) {
  return http({
    url: `${prefix}/safety_weekly_report/key_metrics`,
    method: 'post',
    ...payload,
  }) as Promise<IOverviewDataRaw>;
}

/**
 * 获取子话题四象限数据
 * API: POST /api/v1/opinion_pc/safety_weekly_report/subtopic_quadrant
 */
export function getSubtopicQuadrant(
  payload: any,
): Promise<ISubtopicQuadrantResponse> {
  return http({
    url: `${prefix}/safety_weekly_report/subtopic_quadrant`,
    method: 'post',
    ...payload,
  }) as Promise<ISubtopicQuadrantResponse>;
}

/**
 * 分举报类型声量趋势
 * API: POST /api/v1/opinion_pc/safety_weekly_report/category_mentions_trend
 */
export function getCategoryMentionsTrend(
  payload: any,
): Promise<ICategoryMentionsTrendResponse> {
  return http({
    url: `${prefix}/safety_weekly_report/category_mentions_trend`,
    method: 'post',
    ...payload,
  }) as Promise<ICategoryMentionsTrendResponse>;
}

/**
 * 分子话题声量走势
 * API: POST /api/v1/opinion_pc/safety_weekly_report/topic_timeline
 */
export function getWeeklyReportTopicTimeline(
  payload: any,
): Promise<ISubTopicTrendResponse> {
  return http({
    url: `${prefix}/safety_weekly_report/topic_timeline`,
    method: 'post',
    ...payload,
  }) as Promise<ISubTopicTrendResponse>;
}

/**
 * 获取关键词数据
 */
export function getWeeklyReportKeywordData(
  payload: any,
): Promise<IKeywordResponse> {
  return http({
    url: `${prefix}/safety_weekly_report/keywords`,
    method: 'post',
    ...payload,
  }) as Promise<IKeywordResponse>;
}

/**
 * 获取分维度趋势数据： 渠道和语种
 */
export function getTrendByDimension(payload: any) {
  return http({
    url: `${prefix}/safety_weekly_report/dimension_volume_trend`,
    method: 'post',
    ...payload,
  }) as Promise<ITrendItem>;
}

/**
 * 获取明细表数据
 */
export function getDetailTable(payload: any): Promise<IDetailTableResponse> {
  return http({
    url: `${prefix}/safety_weekly_report/dimension_fluctuation_overview`,
    method: 'post',
    ...payload,
  }) as Promise<IDetailTableResponse>;
}

/**
 * 获取分类筛选器选项
 * API: POST /api/v1/opinion_pc/safety_weekly_report/filter_topic
 */
export const getFilterTopic = (config: {
  data: gameInfoType;
  cancelToken?: any;
  headers?: any;
}): Promise<IFilterTopicResponse> => {
  return http({
    url: `${prefix}/safety_weekly_report/filter_topic`,
    method: 'post',
    ...config,
  }) as Promise<IFilterTopicResponse>;
};
 